import { formatNumber } from "@kvix/shared";
import { Box, Chip, IconButton, LinearProgress, makeStyles } from "@material-ui/core";
import StarIcon from "@material-ui/icons/Star";
import classNames from "classnames";
import { isPast } from "date-fns";
import React, { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { KvixIcon } from "../../../icons";
import { Text } from "../../../Primitives";
const useStyles = makeStyles((theme) => ({
    root: {
        height: "auto",
        borderBottom: `1px solid ${theme.palette.background.paper}`,
        transition: "transform 300ms ease-in-out",
        cursor: "pointer",
        [theme.breakpoints.down("xl")]: {
            paddingRight: theme.spacing(4),
            paddingLeft: theme.spacing(4)
        },
        [theme.breakpoints.down("lg")]: {
            paddingRight: theme.spacing(4),
            paddingLeft: theme.spacing(4)
        },
        [theme.breakpoints.down("md")]: {
            paddingRight: theme.spacing(0),
            paddingLeft: theme.spacing(0)
        },
        [theme.breakpoints.down("sm")]: {
            paddingRight: theme.spacing(2),
            paddingLeft: theme.spacing(2)
        },
        [theme.breakpoints.down("xs")]: {
            paddingTop: theme.spacing(2),
            paddingBottom: theme.spacing(2),
            paddingRight: theme.spacing(2),
            paddingLeft: theme.spacing(2)
        },
        "&:hover": {
            transform: "scale(1.01)"
        },
        "&.last": {
            border: 0
        }
    },
    image: {
        backgroundSize: "cover",
        backgroundPosition: "center",
        height: 90,
        width: 90,
        flexShrink: 0,
        position: "relative",
        borderRadius: "4px",
        "&.imageLoader": {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            "&:after": {
                content: "' '",
                display: "block",
                width: 32,
                height: 32,
                margin: 4,
                borderRadius: "50%",
                border: `2px solid ${theme.palette.primary.main}`,
                borderColor: `${theme.palette.primary.main} transparent ${theme.palette.primary.main} transparent`,
                animation: "$imageLoader 1.2s linear infinite"
            }
        },
        "&.imageFadeIn": {
            animation: "$fadeIn 400ms linear"
        }
    },
    textContainer: {
        display: "flex",
        flexDirection: "column",
        width: "calc(100% - 90px)",
        padding: "6px 12px 0 12px",
        position: "relative"
    },
    title: {
        fontWeight: 700,
        fontSize: 18,
        marginBottom: 3,
        width: "90%"
    },
    subtitle: {
        width: "90%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        marginBottom: 2
    },
    cardDate: {
        fontSize: 12,
        "&.live": {
            fontWeight: 600,
            color: theme.palette.primary.main,
            position: "relative",
            marginLeft: 10,
            "&:before": {
                content: "''",
                background: theme.palette.primary.main,
                width: 6,
                height: 6,
                borderRadius: 3,
                position: "absolute",
                top: 8,
                left: -10
            }
        },
        "&.cancelled": {
            fontStyle: "italic"
        }
    },
    ratingRow: {
        display: "flex",
        alignItems: "center",
        marginRight: -7,
        fontWeight: 700,
        color: theme.palette.getContrastText(theme.palette.background.paper)
    },
    ratingIcon: {
        marginBottom: 0.5,
        marginLeft: 1,
        fontSize: 17,
        "&.MuiSvgIcon-root": {
            fill: theme.palette.getContrastText(theme.palette.background.paper)
        }
    },
    metaContainer: {
        marginTop: 10,
        fontWeight: 500
    },
    metaRow: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap"
    },
    metaChip: {
        margin: "0 4px 4px 0",
        "&.MuiChip-root": {
            background: theme.palette.background.paper,
            borderRadius: 2,
            cursor: "pointer",
            height: 22,
            "& .MuiChip-label": {
                paddingLeft: 6,
                paddingRight: 6,
                fontSize: 13
            }
        },
        "&.withIcon": {
            "&.MuiChip-root": {
                flexDirection: "row-reverse",
                "& .MuiChip-icon": {
                    marginLeft: -6,
                    marginRight: 5,
                    width: "0.7em",
                    height: "0.7em"
                }
            },
            "&.lessMargin": {
                "&.MuiChip-root": {
                    "& .MuiChip-icon": {
                        marginLeft: -9,
                        marginRight: 2
                    }
                }
            }
        }
    },
    favouriteButton: {
        position: "absolute",
        top: 0,
        right: 0,
        color: theme.palette.primary.main,
        textTransform: "initial",
        background: "transparent",
        width: "28px",
        height: "28px",
        boxShadow: "none",
        transition: "transform 200ms ease-in-out",
        "&:hover": {
            transform: "scale(1.1)",
            background: "transparent"
        },
        "&:focus": {
            boxShadow: "none"
        }
    },
    heartIcon: {
        marginTop: "2px",
        fontSize: 22
    },
    descriptionContainer: {
        display: "flex",
        justifyContent: "space-between",
        marginTop: 4,
        [theme.breakpoints.down("xs")]: {
            flexDirection: (props) => (props.rating ? "row" : "column-reverse")
        }
    },
    progressBar: {
        position: "absolute",
        bottom: 0,
        left: 0,
        width: "100%",
        "&.MuiLinearProgress-colorPrimary": {
            background: "transparent"
        }
    },
    "@keyframes fadeIn": {
        "100%": {
            opacity: 1
        },
        "0%": {
            opacity: 0
        }
    },
    "@keyframes imageLoader": {
        "0%": {
            transform: "rotate(0deg)"
        },
        "100%": {
            transform: "rotate(360deg)"
        }
    }
}));
export const CardBase = (props) => {
    const classes = useStyles(props);
    const { t, ready } = useTranslation("cards");
    const imageRef = useRef();
    const image = new Image();
    const { title, instructorName, rating, level, duration, equipment, filterByChip, link, playButton, toggleFavourite, isLiked, noOfFollowers, description, imageUrl, goesLiveAt, start } = props;
    const loadImage = useCallback((ref) => {
        if (ref.current) {
            ref.current.classList.remove("imageLoader");
            ref.current.style.backgroundImage = `linear-gradient(232deg, rgba(255, 255, 255, 0) 0%, rgba(0, 0, 0, 0.48) 100%), url(${image.src})`;
            ref.current.classList.add("imageFadeIn");
        }
    }, []);
    useEffect(() => {
        image.src = imageUrl;
        const fallbackTimeout = setTimeout(() => {
            image.onload = null;
            loadImage(imageRef);
        }, 5000);
        image.onload = () => {
            if (imageRef.current) {
                clearTimeout(fallbackTimeout);
                loadImage(imageRef);
            }
        };
        return () => {
            clearTimeout(fallbackTimeout);
        };
    }, [image, imageUrl, imageRef]);
    const handleChipClick = (e, chip, value) => {
        e.preventDefault();
        filterByChip(chip, value);
    };
    if (!ready) {
        return null;
    }
    return (React.createElement(Link, { to: link },
        React.createElement("div", { className: `${classes.root} ${props.isLast ? "last" : ""}` },
            React.createElement(Box, { display: "flex" },
                React.createElement("div", { className: `${classes.image} imageLoader`, ref: imageRef },
                    playButton,
                    props.progress && (React.createElement(LinearProgress, { color: "primary", variant: "determinate", className: classes.progressBar, value: props.progress > 100 ? 100 : props.progress }))),
                React.createElement("div", { className: classes.textContainer },
                    React.createElement(Text, { variant: "body2", className: classes.title }, title),
                    instructorName && (React.createElement(Text, { variant: "body2", className: classes.subtitle }, instructorName)),
                    React.createElement("div", { className: classes.descriptionContainer },
                        React.createElement("div", null,
                            props.wasLiveAt && (React.createElement(Text, { variant: "body2", className: classes.cardDate }, props.wasLiveAt)),
                            props.goesLiveAt && (React.createElement(React.Fragment, null, props.isCancelled ? (React.createElement(Text, { variant: "body2", className: `${classes.cardDate} cancelled` }, t("isCancelled"))) : props.isStarting ? (React.createElement(Text, { variant: "body2", className: `${classes.cardDate}` }, t("isStarting"))) : props.isLive ? (React.createElement(Text, { variant: "body2", className: `${classes.cardDate} live` }, t("isLive"))) : (React.createElement(Text, { variant: "body2", className: classes.cardDate }, t(isPast(start) ? "sessionStarted" : "sessionStarts", {
                                goesLiveAt
                            }))))),
                            description && React.createElement(Text, { variant: "body2" }, description)),
                        noOfFollowers > 0 && (React.createElement(Text, { variant: "body2" },
                            React.createElement("span", { style: { fontWeight: 600 } }, formatNumber(noOfFollowers)),
                            " f\u00F6ljare")),
                        rating > 0 && (React.createElement("div", { className: classes.ratingRow },
                            React.createElement("span", { style: { fontSize: 12 } }, rating),
                            " ",
                            React.createElement(StarIcon, { className: classes.ratingIcon })))),
                    toggleFavourite && (React.createElement(IconButton, { className: classes.favouriteButton, size: "small", disableRipple: true, disableTouchRipple: true, disableFocusRipple: true, TouchRippleProps: {}, onClickCapture: (e) => {
                            e.preventDefault();
                            toggleFavourite();
                        } }, isLiked ? (React.createElement(KvixIcon.Heart.Filled, { className: classes.heartIcon })) : (React.createElement(KvixIcon.Heart.Empty, { className: classes.heartIcon })))))),
            React.createElement("div", { className: classes.metaContainer },
                React.createElement("div", { className: classes.metaRow },
                    level && (React.createElement(Chip, { label: t(`meta.levels.${level}`), className: classes.metaChip, onClickCapture: (e) => handleChipClick(e, "level", level) })),
                    duration && (React.createElement(Chip, { label: `${duration}`, className: classNames(classes.metaChip, { withIcon: true }), onClickCapture: (e) => handleChipClick(e, "duration", duration), icon: React.createElement(KvixIcon.DurationIcon, null) })),
                    equipment && (React.createElement(Chip, { className: classNames(classes.metaChip, {
                            withIcon: true,
                            lessMargin: true
                        }), onClickCapture: (e) => handleChipClick(e, "equipment", equipment), icon: React.createElement(KvixIcon.EquipmentIcon, null) })))))));
};
//# sourceMappingURL=CardBase.js.map